import {useState, useEffect, useRef} from 'react'
import type {GenericContent} from '../../../../../../brand/lib/types/contentful'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import {Heading, Image, Text} from '@primer/react-brand'
import {ArrowLeftIcon, ArrowRightIcon} from '@primer/octicons-react'
import {Label} from '../../_shared/Label/Label'

import Styles from './Gallery.module.css'

type Props = {
  content: GenericContent
  sectionIndex: number
}

export function Gallery(props: Props) {
  const {content, sectionIndex} = props
  const {heading, label, media} = content.fields

  const title = heading
  const eyebrow = label?.fields.text

  // Gallery pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(1)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth >= 1280) {
        setItemsPerPage(3)
      } else if (window.innerWidth >= 804) {
        setItemsPerPage(2)
      } else {
        setItemsPerPage(1)
      }
      setCurrentPage(1) // Reset to first page on resize
      setIsInitialized(true)
    }

    updateItemsPerPage()

    const resizeObserver = new ResizeObserver(() => {
      updateItemsPerPage()
    })

    resizeObserver.observe(document.documentElement)

    return () => resizeObserver.disconnect()
  }, [])

  const totalPages =
    Array.isArray(media) && media.length > 0 && itemsPerPage > 0 ? Math.ceil(media.length / itemsPerPage) : 0

  const handlePrevious = () => {
    setDirection('backward')
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNext = () => {
    setDirection('forward')
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  // Animation
  const wrapper1Ref = useRef<HTMLDivElement>(null)
  const wrapper2Ref = useRef<HTMLDivElement>(null)

  const headingRef = useRef<HTMLDivElement>(null)
  const assetRefsArray = useRef<Array<HTMLDivElement | null>>([])

  useEffect(() => {
    const elements1: AnimationElement[] = []
    if (headingRef.current) elements1.push({element: headingRef.current})
    const cleanup1 = wrapper1Ref.current ? animateFadeIn(elements1, wrapper1Ref.current) : undefined

    const elements2: AnimationElement[] = []
    for (let i = 0; i < itemsPerPage; i++) {
      if (assetRefsArray.current[i]) {
        elements2.push({element: assetRefsArray.current[i]!, animationType: 'asset'})
      }
    }
    const cleanup2 = wrapper2Ref.current ? animateFadeIn(elements2, wrapper2Ref.current) : undefined

    return () => {
      cleanup1?.()
      cleanup2?.()
    }
  }, [itemsPerPage])

  return (
    <div className={Styles.Gallery}>
      <div className={Styles.Wrapper} ref={wrapper1Ref}>
        <div className={Styles.Cell}>
          {eyebrow && <Label className={Styles.Eyebrow} text={eyebrow} />}

          {title && (
            <Heading className={Styles.Title} data-heading="1" as={sectionIndex === 0 ? 'h2' : 'h3'} ref={headingRef}>
              {title}
            </Heading>
          )}
        </div>

        <div className={Styles.Cell}>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className={Styles.Pagination} style={{visibility: isInitialized ? 'visible' : 'hidden'}}>
              <button
                className={Styles.PaginationButton}
                aria-label={t('Previous page')}
                aria-disabled={currentPage === 1}
                onClick={handlePrevious}
              >
                <ArrowLeftIcon />
              </button>

              <Text className={Styles.PaginationLabel} size="200" variant="muted">
                {currentPage} / {totalPages}
              </Text>

              <button
                className={Styles.PaginationButton}
                aria-label={t('Next page')}
                aria-disabled={currentPage === totalPages}
                onClick={handleNext}
              >
                <ArrowRightIcon />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={Styles.Grid}
        data-direction={direction}
        data-items={media && media.length >= 3 ? 3 : 2}
        style={{visibility: isInitialized ? 'visible' : 'hidden'}}
        ref={wrapper2Ref}
      >
        {media?.map((item, itemIndex) => {
          const figure = item.fields
          const {asset} = figure || {}

          const startIndex = (currentPage - 1) * itemsPerPage
          const endIndex = startIndex + itemsPerPage
          const isVisible = itemIndex >= startIndex && itemIndex < endIndex

          return figure ? (
            <figure
              className={Styles.Figure}
              key={item.sys?.id || itemIndex}
              data-visible={isVisible}
              style={{'--delay': `${(itemIndex - startIndex) * 60}ms`}}
            >
              {asset?.fields.file.details?.image && (
                <div
                  ref={el => {
                    assetRefsArray.current[itemIndex] = el
                  }}
                  className={Styles.ImageWrapper}
                  data-animate="container"
                >
                  <div data-animate="item">
                    <Image
                      className={Styles.Image}
                      src={asset.fields.file.url}
                      width={figure.width || asset.fields.file.details?.image?.width || ''}
                      height={figure.height || asset.fields.file.details?.image?.height || ''}
                      alt={asset.fields.description || ''}
                      loading="lazy"
                    />
                  </div>
                  <div data-animate="cover" />
                </div>
              )}

              {figure.description && (
                <figcaption className={Styles.Caption}>
                  <Text size="300" variant="muted">
                    {figure.description}
                  </Text>
                </figcaption>
              )}
            </figure>
          ) : null
        })}
      </div>
    </div>
  )
}
